<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useTheme } from 'vuetify';
import { importLibrary } from '@/utils/google-maps-loader';
import { useCompanyMapCenter, COUNTRY_ZOOM } from '@/composables/useCompanyMapCenter';
import type { ShiftTodayListItem } from '@/api/shifts';
import { groupShiftsByLocation, locationStatusColor, type ShiftLocation } from './shifts-today-map.utils';

const props = defineProps<{
  shifts: ShiftTodayListItem[];
  loading: boolean;
}>();

// Street level is enough to tell patients apart; closer only adds noise.
const MAX_FIT_ZOOM = 15;
const FIT_BOUNDS_PADDING = 48;
const PIN_SIZE_PX = 28;
const FALLBACK_PIN_COLOR = '#9e9e9e';

const theme = useTheme();
const companyMapCenter = useCompanyMapCenter();

const mapContainer = ref<HTMLElement | null>(null);
const mapError = ref(false);

let map: google.maps.Map | null = null;
let infoWindow: google.maps.InfoWindow | null = null;
let markers: google.maps.marker.AdvancedMarkerElement[] = [];
let hasFitBounds = false;

const locations = computed(() => groupShiftsByLocation(props.shifts));
const mappedCount = computed(() => locations.value.reduce((sum, loc) => sum + loc.shifts.length, 0));

function pluralize(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function pinColor(location: ShiftLocation) {
  const color = locationStatusColor(location);

  return theme.current.value.colors[color] ?? FALLBACK_PIN_COLOR;
}

function createPin(location: ShiftLocation): HTMLElement {
  const pin = document.createElement('div');
  pin.textContent = location.shifts.length > 1 ? String(location.shifts.length) : '';
  Object.assign(pin.style, {
    width: `${PIN_SIZE_PX}px`,
    height: `${PIN_SIZE_PX}px`,
    borderRadius: '50%',
    border: '2px solid #fff',
    boxShadow: '0 1px 4px rgba(0, 0, 0, .4)',
    backgroundColor: pinColor(location),
    color: '#fff',
    fontSize: '13px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  return pin;
}

function textLine(text: string, style: Partial<CSSStyleDeclaration> = {}) {
  const line = document.createElement('div');
  line.textContent = text;
  Object.assign(line.style, style);

  return line;
}

// Built from DOM nodes (textContent), never interpolated HTML: names are user data.
function buildInfoContent(location: ShiftLocation): HTMLElement {
  const content = document.createElement('div');
  content.style.lineHeight = '1.4';
  location.shifts.forEach((shift, index) => {
    const entry = document.createElement('div');
    if (index > 0) entry.style.marginTop = '8px';
    const caregiver = [shift.caregiverFullName, shift.caregiverHumanCareer].filter(Boolean).join(' · ');
    entry.append(
      textLine(shift.patientFullName, { fontWeight: '600' }),
      textLine(caregiver),
      textLine(`${shift.humanTime} · ${shift.status.name}`, { color: '#666' }),
    );
    content.appendChild(entry);
  });

  return content;
}

function markerTitle(location: ShiftLocation) {
  return location.shifts.map(shift => `${shift.patientFullName} (${shift.status.name})`).join(', ');
}

function clearMarkers() {
  markers.forEach((marker) => {
    marker.map = null;
  });
  markers = [];
}

// Frames the markers once: later refreshes keep whatever zoom the user chose.
function fitToMarkers() {
  if (!map || hasFitBounds || locations.value.length === 0) return;

  const bounds = new google.maps.LatLngBounds();
  locations.value.forEach(location => bounds.extend(location.position));
  map.fitBounds(bounds, FIT_BOUNDS_PADDING);
  const zoom = map.getZoom();
  if (zoom && zoom > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
  hasFitBounds = true;
}

async function renderMarkers() {
  if (!map) return;

  const { AdvancedMarkerElement } = await importLibrary('marker');
  if (!map) return; // unmounted while the library loaded
  clearMarkers();
  infoWindow?.close();
  locations.value.forEach(location => {
    const marker = new AdvancedMarkerElement({
      map,
      position: location.position,
      content: createPin(location),
      title: markerTitle(location),
      gmpClickable: true,
    });
    marker.addEventListener('gmp-click', () => {
      infoWindow?.setContent(buildInfoContent(location));
      infoWindow?.open({ map, anchor: marker });
    });
    markers.push(marker);
  });
  fitToMarkers();
}

// The minute refresh returns new objects even when nothing moved: redraw only
// when a marker's position, shifts or status changed, so an open popup survives.
const markersSignature = computed(() => JSON.stringify(locations.value.map(location => [
  location.position, location.shifts.map(shift => [shift.id, shift.status.color]),
])));

watch(markersSignature, renderMarkers);

onMounted(async () => {
  if (!mapContainer.value) return;

  try {
    const { Map, InfoWindow } = await importLibrary('maps');
    map = new Map(mapContainer.value, {
      center: companyMapCenter.value,
      zoom: COUNTRY_ZOOM,
      mapId: import.meta.env.VITE_GOOGLE_MAPS_MAP_ID,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'cooperative',
    });
    infoWindow = new InfoWindow();
    await renderMarkers();
  } catch {
    mapError.value = true;
  }
});

onUnmounted(() => {
  infoWindow?.close();
  clearMarkers();
  map = null;
});
</script>
<template>
  <v-card elevation="2">
    <div class="d-flex flex-wrap align-center ga-2 px-4 py-3">
      <span class="text-subtitle-2 font-weight-bold">Turnos activos en vivo</span>
      <v-spacer />
      <span
        v-if="!loading"
        class="text-body-2 text-medium-emphasis"
      >
        {{ pluralize(shifts.length, 'turno activo', 'turnos activos') }}
        <template v-if="mappedCount < shifts.length">
          · {{ mappedCount }} en mapa
        </template>
      </span>
    </div>
    <v-divider />
    <div class="today-map">
      <div
        ref="mapContainer"
        class="today-map__canvas"
        role="region"
        aria-label="Mapa de turnos activos"
      />
      <div
        v-if="mapError"
        class="today-map__overlay text-error"
      >
        No se pudo cargar el mapa.
      </div>
      <div
        v-else-if="loading"
        class="today-map__overlay"
      >
        Cargando mapa...
      </div>
      <div
        v-else-if="shifts.length === 0"
        class="today-map__overlay"
      >
        No hay turnos activos en este momento.
      </div>
    </div>
  </v-card>
</template>
<style scoped>
.today-map {
  position: relative;
  height: 450px;
}

.today-map__canvas {
  width: 100%;
  height: 100%;
}

.today-map__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  background: rgba(255, 255, 255, .85);
  pointer-events: none;
}

@media (max-width: 600px) {
  .today-map {
    height: 360px;
  }
}
</style>
